// DatePickerField.js
import React, { useState, useRef, useEffect } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './date-picker.scss';

export const DatePickerField = ({
    value,
    onChange,
    onBlur,
    error,
    touched,
    disabled,
    label,
    placeholder = 'Seleziona data',
    name,
    maxDate = new Date()
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [viewMode, setViewMode] = useState('days'); // 'days' | 'months' | 'years'
    const wrapperRef = useRef(null);

    // Gestione click fuori
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setViewMode('days');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Aggiorna selectedDate quando value cambia
    useEffect(() => {
        if (value) {
            setSelectedDate(new Date(value));
        }
    }, [value]);

    const formatDate = (date) => {
        if (!date) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];
        
        // Giorni del mese precedente
        const firstDayOfWeek = firstDay.getDay();
        const prevMonthLastDay = new Date(year, month, 0).getDate();
        for (let i = firstDayOfWeek - 1; i >= 0; i--) {
            days.push({
                day: prevMonthLastDay - i,
                isOtherMonth: true,
                date: new Date(year, month - 1, prevMonthLastDay - i)
            });
        }

        // Giorni del mese corrente
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push({
                day: i,
                isOtherMonth: false,
                date: new Date(year, month, i)
            });
        }

        // Giorni del mese successivo
        const remainingDays = 42 - days.length;
        for (let i = 1; i <= remainingDays; i++) {
            days.push({
                day: i,
                isOtherMonth: true,
                date: new Date(year, month + 1, i)
            });
        }

        return days;
    };

    const handleDateSelect = (date) => {
        if (disabled) return;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date > today) {
            return;
        }

        setSelectedDate(date);
        onChange({
            target: {
                name: name,
                value: date.toISOString().split('T')[0]
            }
        });
        setIsOpen(false);
        setViewMode('days');
    };

    const changeMonth = (increment) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + increment);
        setCurrentMonth(newMonth);
    };

    const changeYear = (increment) => {
        const newYear = new Date(currentMonth);
        newYear.setFullYear(newYear.getFullYear() + increment);
        setCurrentMonth(newYear);
    };

    const isDateDisabled = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date > today;
    };

    const isDateSelected = (date) => {
        if (!selectedDate) return false;
        return date.getDate() === selectedDate.getDate() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getFullYear() === selectedDate.getFullYear();
    };

    const isToday = (date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
                        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
    const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

    // Genera anni per la selezione (dal 1900 all'anno corrente)
    const getYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear; year >= 1900; year--) {
            years.push(year);
        }
        return years;
    };

    const handleMonthSelect = (monthIndex) => {
        const newDate = new Date(currentMonth);
        newDate.setMonth(monthIndex);
        setCurrentMonth(newDate);
        setViewMode('days');
    };

    const handleYearSelect = (year) => {
        const newDate = new Date(currentMonth);
        newDate.setFullYear(year);
        setCurrentMonth(newDate);
        setViewMode('months');
    };

    const toggleViewMode = () => {
        if (viewMode === 'days') {
            setViewMode('months');
        } else if (viewMode === 'months') {
            setViewMode('years');
        } else {
            setViewMode('days');
        }
    };

    return (
        <div className="date-picker-wrapper" ref={wrapperRef}>
            <label htmlFor={name} className="field__label">
                {label}
            </label>
            <div className="date-picker-input-wrapper">
                <input
                    type="text"
                    id={name}
                    name={name}
                    className={`input__elt ${touched && error ? 'error' : ''}`}
                    placeholder={placeholder}
                    value={selectedDate ? formatDate(selectedDate) : ''}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    onBlur={onBlur}
                    disabled={disabled}
                    readOnly
                />
                <button
                    type="button"
                    className="date-picker-icon"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                >
                    <FiCalendar />
                </button>
            </div>
            {touched && error && (
                <span className="error-message">{error}</span>
            )}

            {isOpen && !disabled && (
                <div className="date-picker-dropdown">
                    {/* Header con navigazione */}
                    <div className="date-picker-header">
                        <button 
                            type="button" 
                            onClick={() => {
                                if (viewMode === 'days') changeMonth(-1);
                                else if (viewMode === 'months') changeYear(-1);
                                else changeYear(-12);
                            }}
                            className="date-nav-button"
                        >
                            <FiChevronLeft />
                        </button>
                        
                        <button 
                            type="button" 
                            className="date-picker-month-year"
                            onClick={toggleViewMode}
                        >
                            {viewMode === 'days' && (
                                <span>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
                            )}
                            {viewMode === 'months' && (
                                <span>{currentMonth.getFullYear()}</span>
                            )}
                            {viewMode === 'years' && (
                                <span>Seleziona anno</span>
                            )}
                        </button>
                        
                        <button 
                            type="button" 
                            onClick={() => {
                                if (viewMode === 'days') changeMonth(1);
                                else if (viewMode === 'months') changeYear(1);
                                else changeYear(12);
                            }}
                            className="date-nav-button"
                        >
                            <FiChevronRight />
                        </button>
                    </div>

                    {/* Visualizzazione giorni */}
                    {viewMode === 'days' && (
                        <>
                            <div className="date-picker-weekdays">
                                {dayNames.map(day => (
                                    <div key={day} className="date-picker-weekday">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            <div className="date-picker-days">
                                {getDaysInMonth(currentMonth).map((item, index) => {
                                    const isDisabled = isDateDisabled(item.date);
                                    const isSelected = isDateSelected(item.date);
                                    const isTodayDate = isToday(item.date);

                                    return (
                                        <button
                                            key={index}
                                            type="button"
                                            className={`date-picker-day 
                                                ${item.isOtherMonth ? 'other-month' : ''} 
                                                ${isSelected ? 'selected' : ''} 
                                                ${isTodayDate ? 'today' : ''}
                                                ${isDisabled ? 'disabled' : ''}
                                            `}
                                            onClick={() => !isDisabled && handleDateSelect(item.date)}
                                            disabled={isDisabled}
                                        >
                                            {item.day}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* Visualizzazione mesi */}
                    {viewMode === 'months' && (
                        <div className="date-picker-months">
                            {monthNames.map((month, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={`date-picker-month-item ${
                                        currentMonth.getMonth() === index ? 'active' : ''
                                    }`}
                                    onClick={() => handleMonthSelect(index)}
                                >
                                    {month}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Visualizzazione anni */}
                    {viewMode === 'years' && (
                        <div className="date-picker-years">
                            {getYears().map((year) => (
                                <button
                                    key={year}
                                    type="button"
                                    className={`date-picker-year-item ${
                                        currentMonth.getFullYear() === year ? 'active' : ''
                                    }`}
                                    onClick={() => handleYearSelect(year)}
                                >
                                    {year}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};